import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2, Save, Eye, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { companyAPI, rolesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { IT_SETTINGS_TABS as ALL_TABS } from '../config/itSettingsTabs';

import Devices from './Devices';
import AuditLog from './AuditLog';
import AdminAuditHub from './AdminAuditHub';
import EmailSettings from './EmailSettings';
import SchulferienManager from './SchulferienManager';
import { SecurityCard } from './companysettings/SecurityCard';
import { ReminderCard } from './companysettings/ReminderCard';

export default function ITSettings() {
  const { user, isAdmin, realIsAdmin, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const roleId = user?.effective_permissions?.role_id || null;
  const TABS = ALL_TABS.filter((t) => t.can({ isAdmin, hasPermission, roleId }));
  const requestedTab = searchParams.get('tab');
  const initialTab = TABS.some((t) => t.value === requestedTab)
    ? requestedTab
    : (TABS[0]?.value || '');
  const [tab, setTab] = useState(initialTab);

  // Rollen-Vorschau (nur für echte Admins)
  const [roles, setRoles] = useState([]);
  const [previewRoleId, setPreviewRoleId] = useState('');

  // Firmen-Einstellungen (für Sicherheit & Stempel-Erinnerungen)
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await companyAPI.get();
        const sanitized = Object.fromEntries(
          Object.entries(res.data || {}).map(([k, v]) => [k, v === null ? '' : v]),
        );
        setFormData({
          ...sanitized,
          holiday_auto_credit: sanitized.holiday_auto_credit !== false,
          ko_tag_enabled: sanitized.ko_tag_enabled !== false,
        });
      } catch (e) {
        console.error('Firmen-Einstellungen laden fehlgeschlagen', e);
        setFormData({});
      }
    })();
  }, []);

  useEffect(() => {
    if (!realIsAdmin) return;
    (async () => {
      try {
        const res = await rolesAPI.getAll();
        setRoles(res.data || []);
      } catch (e) {
        console.error('Rollen laden fehlgeschlagen', e);
      }
    })();
  }, [realIsAdmin]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await companyAPI.update(formData);
      toast.success('Einstellungen gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Berechnet die für eine Rolle sichtbaren Tabs (gleiche Logik wie die echte Filterung)
  const previewRole = roles.find((r) => r.id === previewRoleId);
  const LEVELS = { none: 0, view: 1, execute: 2, edit: 3 };
  const previewVisibility = previewRole
    ? ALL_TABS.map((t) => {
        const ctx = {
          isAdmin: previewRole.id === 'role-admin',
          roleId: previewRole.id,
          hasPermission: (area, level = 'view') =>
            (LEVELS[previewRole.permissions?.[area] || 'none'] || 0) >= (LEVELS[level] || 0),
        };
        return { tab: t, visible: t.can(ctx) };
      })
    : [];

  const changeTab = (v) => {
    setTab(v);
    setSearchParams({ tab: v }, { replace: true });
  };

  const canShow = (v) => TABS.some((t) => t.value === v);

  if (TABS.length === 0) {
    return (
      <DashboardLayout>
        <div className="py-20 text-center text-muted-foreground" data-testid="it-settings-no-access">
          Sie haben keine Berechtigung für die IT-Einstellungen.
        </div>
      </DashboardLayout>
    );
  }

  const SaveBar = (
    <div className="flex justify-end pt-2">
      <Button onClick={handleSaveCompany} disabled={saving || !formData} data-testid="it-settings-save">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Speichern
      </Button>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="it-settings-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">IT-Einstellungen</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Geräte, Protokolle, Sicherheit, Erinnerungen, Schulferien und E-Mail an einem Ort.
          </p>
        </div>

        {realIsAdmin && (
          <div className="rounded-xl border bg-muted/30 p-4" data-testid="role-preview-panel">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2 shrink-0">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Rollen-Vorschau</span>
              </div>
              <Select value={previewRoleId} onValueChange={setPreviewRoleId}>
                <SelectTrigger className="sm:w-72" data-testid="role-preview-select">
                  <SelectValue placeholder="Rolle wählen, um sichtbare Tabs zu prüfen…" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`role-preview-option-${r.id}`}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {previewRole && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="role-preview-result">
                {previewVisibility.map(({ tab: t, visible }) => (
                  <Badge
                    key={t.value}
                    variant={visible ? 'default' : 'outline'}
                    className={`gap-1 ${visible ? '' : 'text-muted-foreground line-through opacity-60'}`}
                    data-testid={`role-preview-tab-${t.value}-${visible ? 'visible' : 'hidden'}`}
                  >
                    {visible ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {t.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        <Tabs value={tab} onValueChange={changeTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-1 justify-start" data-testid="it-settings-tabs">
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="gap-1.5" data-testid={`it-tab-${t.value}`}>
                <t.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {canShow('devices') && (
            <TabsContent value="devices" className="mt-6">
              <Devices embedded />
            </TabsContent>
          )}
          {canShow('audit-log') && (
            <TabsContent value="audit-log" className="mt-6">
              <AuditLog embedded />
            </TabsContent>
          )}
          {canShow('diagnose') && (
            <TabsContent value="diagnose" className="mt-6">
              <AdminAuditHub embedded />
            </TabsContent>
          )}
          {canShow('security') && (
            <TabsContent value="security" className="mt-6">
              {formData ? (
                <div className="space-y-4 max-w-3xl">
                  <SecurityCard formData={formData} onChange={handleChange} />
                  {SaveBar}
                </div>
              ) : (
                <Skeleton className="h-72 rounded-xl" />
              )}
            </TabsContent>
          )}
          {canShow('reminder') && (
            <TabsContent value="reminder" className="mt-6">
              {formData ? (
                <div className="space-y-4 max-w-3xl">
                  <ReminderCard formData={formData} onChange={handleChange} />
                  {SaveBar}
                </div>
              ) : (
                <Skeleton className="h-72 rounded-xl" />
              )}
            </TabsContent>
          )}
          {canShow('schulferien') && (
            <TabsContent value="schulferien" className="mt-6">
              <SchulferienManager />
            </TabsContent>
          )}
          {canShow('email') && (
            <TabsContent value="email" className="mt-6">
              <EmailSettings embedded />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
